/**
 * ConfigEditor Component
 * Module 07 - Dashboard UI
 * Section 06 - Embeds Management (Step 6.2 - Part 1)
 * 
 * Editor for embed configuration settings including lighting, camera, environment, and controls
 */

import React from 'react';
import { EmbedSettings, LightingPreset, CameraViewpoint, BackgroundType, EnvironmentPreset } from '../../types';

interface ConfigEditorProps {
  settings: EmbedSettings;
  onChange: (settings: EmbedSettings) => void;
}

// Helper function to format preset names to proper case
const formatPresetName = (preset: string): string => {
  // Handle special cases
  if (preset === 'fabricDetail') return 'Fabric Detail';
  if (preset === 'envMap') return 'Env Map';
  
  // Standard title case: capitalize first letter
  return preset.charAt(0).toUpperCase() + preset.slice(1);
};

/**
 * ConfigEditor allows users to configure all aspects of the 3D viewer embed
 * Sections: Lighting, Camera, Environment, Controls
 */
const ConfigEditor: React.FC<ConfigEditorProps> = ({ settings, onChange }) => {
  
  // Ensure settings has all required properties with defaults
  const safeSettings: EmbedSettings = {
    lighting: settings?.lighting || { preset: 'neutral', intensity: 1.0 },
    camera: settings?.camera || { fov: 45, initialViewpoint: 'front', autoFrame: true },
    environment: settings?.environment || { background: 'transparent', backgroundColor: '#ffffff', envMapPreset: 'none' },
    controls: settings?.controls || { enableRotate: true, enableZoom: true, enablePan: false, autoRotate: false, autoRotateSpeed: 2 },
  };
  
  const updateLighting = (updates: Partial<typeof safeSettings.lighting>) => {
    onChange({
      ...safeSettings,
      lighting: { ...safeSettings.lighting, ...updates },
    });
  };

  const updateCamera = (updates: Partial<typeof safeSettings.camera>) => {
    onChange({
      ...safeSettings,
      camera: { ...safeSettings.camera, ...updates },
    });
  };

  const updateEnvironment = (updates: Partial<typeof safeSettings.environment>) => {
    onChange({
      ...safeSettings,
      environment: { ...safeSettings.environment, ...updates },
    });
  };

  const updateControls = (updates: Partial<typeof safeSettings.controls>) => {
    onChange({
      ...safeSettings,
      controls: { ...safeSettings.controls, ...updates },
    });
  };

  const lightingPresets: LightingPreset[] = [
    'neutral',
    'runway',
    'daylight',
    'retail',
    'fabricDetail',
    'soft',
    'dramatic',
    'outdoor',
  ];

  const cameraViewpoints: CameraViewpoint[] = [
    'front',
    'back',
    'left',
    'right',
    'top',
    'bottom',
  ];

  const backgroundTypes: BackgroundType[] = [
    'transparent',
    'solid',
    'gradient',
    'envMap',
  ];

  const environmentPresets: EnvironmentPreset[] = [
    'none',
    'studio',
    'outdoor',
    'sunset',
    'neutral',
  ];

  return (
    <div className="space-y-6">
      {/* Lighting Configuration */}
      <div className="border-b border-gray-200 pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Lighting</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lighting Preset
            </label>
            <select
              value={safeSettings.lighting.preset}
              onChange={(e) => updateLighting({ preset: e.target.value as LightingPreset })}
              className="input-field"
            >
              {lightingPresets.map((preset) => (
                <option key={preset} value={preset}>
                  {formatPresetName(preset)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Choose from predefined lighting setups optimised for fashion photography
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intensity: {safeSettings.lighting.intensity.toFixed(1)}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={safeSettings.lighting.intensity}
              onChange={(e) => updateLighting({ intensity: parseFloat(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Dim</span>
              <span>Normal</span>
              <span>Bright</span>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Configuration */}
      <div className="border-b border-gray-200 pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Camera</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Initial Viewpoint
            </label>
            <select
              value={safeSettings.camera.initialViewpoint}
              onChange={(e) => updateCamera({ initialViewpoint: e.target.value as CameraViewpoint })}
              className="input-field"
            >
              {cameraViewpoints.map((viewpoint) => (
                <option key={viewpoint} value={viewpoint}>
                  {viewpoint.charAt(0).toUpperCase() + viewpoint.slice(1)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              The camera angle when the viewer first loads
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Field of View: {safeSettings.camera.fov}°
            </label>
            <input
              type="range"
              min="15"
              max="75"
              step="5"
              value={safeSettings.camera.fov}
              onChange={(e) => updateCamera({ fov: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Narrow (15°)</span>
              <span>Wide (75°)</span>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoFrame"
              checked={safeSettings.camera.autoFrame}
              onChange={(e) => updateCamera({ autoFrame: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="autoFrame" className="ml-2 block text-sm text-gray-900">
              Auto-frame model to fit viewport
            </label>
          </div>
        </div>
      </div>

      {/* Environment Configuration */}
      <div className="border-b border-gray-200 pb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Environment</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background
            </label>
            <select
              value={safeSettings.environment.background}
              onChange={(e) => updateEnvironment({ background: e.target.value as BackgroundType })}
              className="input-field"
            >
              {backgroundTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1).replace(/([A-Z])/g, ' $1')}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Choose how the background appears behind the model
            </p>
          </div>

          {safeSettings.environment.background === 'solid' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Colour
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={safeSettings.environment.backgroundColor || '#ffffff'}
                  onChange={(e) => updateEnvironment({ backgroundColor: e.target.value })}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={safeSettings.environment.backgroundColor || '#ffffff'}
                  onChange={(e) => updateEnvironment({ backgroundColor: e.target.value })}
                  className="input-field flex-1"
                  placeholder="#ffffff"
                />
              </div>
            </div>
          )}

          {safeSettings.environment.background === 'gradient' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gradient Top Colour
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={safeSettings.environment.gradientTop || '#ffffff'}
                    onChange={(e) => updateEnvironment({ gradientTop: e.target.value })}
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={safeSettings.environment.gradientTop || '#ffffff'}
                    onChange={(e) => updateEnvironment({ gradientTop: e.target.value })}
                    className="input-field flex-1"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gradient Bottom Colour
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={safeSettings.environment.gradientBottom || '#999999'}
                    onChange={(e) => updateEnvironment({ gradientBottom: e.target.value })}
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={safeSettings.environment.gradientBottom || '#999999'}
                    onChange={(e) => updateEnvironment({ gradientBottom: e.target.value })}
                    className="input-field flex-1"
                    placeholder="#999999"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Environment Map
            </label>
            <select
              value={safeSettings.environment.envMapPreset}
              onChange={(e) => updateEnvironment({ envMapPreset: e.target.value as EnvironmentPreset })}
              className="input-field"
            >
              {environmentPresets.map((preset) => (
                <option key={preset} value={preset}>
                  {preset.charAt(0).toUpperCase() + preset.slice(1)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              HDR environment map for realistic reflections and ambient lighting
            </p>
          </div>
        </div>
      </div>

      {/* Controls Configuration */}
      <div className="pb-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Controls</h3>
        
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableRotate"
              checked={safeSettings.controls.enableRotate}
              onChange={(e) => updateControls({ enableRotate: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableRotate" className="ml-2 block text-sm text-gray-900">
              Enable rotation (drag to rotate)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableZoom"
              checked={safeSettings.controls.enableZoom}
              onChange={(e) => updateControls({ enableZoom: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableZoom" className="ml-2 block text-sm text-gray-900">
              Enable zoom (scroll or pinch)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="enablePan"
              checked={safeSettings.controls.enablePan}
              onChange={(e) => updateControls({ enablePan: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enablePan" className="ml-2 block text-sm text-gray-900">
              Enable panning (right-click drag)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoRotate"
              checked={safeSettings.controls.autoRotate}
              onChange={(e) => updateControls({ autoRotate: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="autoRotate" className="ml-2 block text-sm text-gray-900">
              Auto-rotate (continuous rotation)
            </label>
          </div>

          {safeSettings.controls.autoRotate && (
            <div className="ml-6 mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto-rotate Speed: {safeSettings.controls.autoRotateSpeed || 2}
              </label>
              <input
                type="range"
                min="1"
                max="10"
                step="1"
                value={safeSettings.controls.autoRotateSpeed || 2}
                onChange={(e) => updateControls({ autoRotateSpeed: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Slow</span>
                <span>Fast</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-800">
          <strong>💡 Tip:</strong> These settings will be applied to the embedded 3D viewer. 
          You can preview the configuration after creating the embed.
        </p>
      </div>
    </div>
  );
};

export default ConfigEditor;
